import { cn } from '../../lib/cn.ts';
import { Button } from '../button.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select.tsx';

export interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  pageSizes?: number[];
  onChange: (next: { offset: number; limit: number }) => void;
  /** Formats the "1–50 of 128" summary (i18n-supplied). */
  rangeLabel: (from: number, to: number, total: number) => string;
  prevLabel: string;
  nextLabel: string;
  pageSizeLabel: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Pagination — Prev/Next + page-size selector + an "X–Y of N" summary. Offset
 * based; disables at the ends. Changing the page size resets to the first page.
 */
export function Pagination({
  total,
  limit,
  offset,
  pageSizes = [25, 50, 100],
  onChange,
  rangeLabel,
  prevLabel,
  nextLabel,
  pageSizeLabel,
  className,
  'data-testid': testId,
}: PaginationProps) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  const atStart = offset <= 0;
  const atEnd = offset + limit >= total;

  return (
    <div
      data-testid={testId}
      className={cn('flex flex-wrap items-center justify-between gap-3 pt-4', className)}
    >
      <span
        data-testid={testId ? `${testId}-summary` : undefined}
        className="font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]"
      >
        {rangeLabel(from, to, total)}
      </span>
      <div className="flex items-center gap-2">
        <Select
          value={String(limit)}
          onValueChange={(v) => onChange({ offset: 0, limit: Number(v) })}
        >
          <SelectTrigger
            data-testid={testId ? `${testId}-size` : undefined}
            aria-label={pageSizeLabel}
            className="h-8 w-24"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizes.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          data-testid={testId ? `${testId}-prev` : undefined}
          size="sm"
          variant="secondary"
          disabled={atStart}
          onClick={() => onChange({ offset: Math.max(offset - limit, 0), limit })}
        >
          {prevLabel}
        </Button>
        <Button
          data-testid={testId ? `${testId}-next` : undefined}
          size="sm"
          variant="secondary"
          disabled={atEnd}
          onClick={() => onChange({ offset: offset + limit, limit })}
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}
